import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getInventoryService, deleteInventoryService } from '@/services/inventoryService';

import Breadcrumb from '@/components/common/Breadcrumb';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import CustomDropdown from '@/components/common/CustomDropdown';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import PritntPdfTable from '@/components/common/PritntPdfTable';
import PermissionCheck from '@/components/common/PermissionCheck';
import { cn } from '@/lib/utils';
import validationErrors from '@/lib/validationErrors';
import ToastMe from '@/components/ui/ToastMe';

import DataTableFilter from '@/views/admin/inventory/component/DataTableFilter';
import DataTable from '@/views/admin/inventory/component/DataTable';
import AddEditInventoryDrawer from '@/views/admin/inventory/component/AddEditInventoryDrawer';

const InventoryPage = ({ pageTitle }) => {
  const dispatch = useDispatch();
  const { printTableRef, handleDownloadPdf } = PritntPdfTable('Inventory Data');

  // “All branches” → show Branch column
  const rawBranchPref = (typeof localStorage !== 'undefined' && localStorage.getItem('branch_id')) || '';
  const showBranchCol = !rawBranchPref || rawBranchPref.toLowerCase() === 'all' || rawBranchPref === '*';

  const [inventoryData, setInventoryData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDataId, setEditDataId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const filterDivRef = useRef(null);

  const [dataFilter, setDataFilter] = useState({
    search: '',
    page: 1,
    limit: 10,
    extra_filter: {},
  });

  const tableColumn = useMemo(() => {
    const cols = [
      { name: 'Item Name', isIncludePrint: true },
      { name: 'Category', isIncludePrint: true },
    ];
    if (showBranchCol) cols.push({ name: 'Branch', isIncludePrint: true });
    cols.push(
      { name: 'Perishable', isIncludePrint: true },
      { name: 'Quantity', isIncludePrint: true },
      { name: 'Price', isIncludePrint: true },
      { name: 'Status', isIncludePrint: true },
      { name: 'Action', isIncludePrint: false }
    );
    return cols;
  }, [showBranchCol]);

  useEffect(() => {
    getTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilter]);

  const getTableData = () => {
    setIsLoading(true);
    dispatch(getInventoryService(dataFilter))
      .then((res) => {
        const payload = res?.data?.data;
        setInventoryData(payload?.data || []);
        setTotalItems(payload?.total || 0);
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message))
      .finally(() => setIsLoading(false));
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    dispatch(deleteInventoryService(id))
      .then(() => {
        ToastMe('Record deleted successfully', 'success');
        getTableData();
      })
      .catch(({ message, errorData }) => validationErrors(errorData, message));
  };

  const handleOpenDrawer = (id = '') => {
    setEditDataId(id);
    setDrawerOpen(true);
    document.body.style.overflowY = 'hidden';
  };
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditDataId('');
    document.body.style.overflowY = 'auto';
    getTableData();
  };

  const handleFilterToggle = () => {
    if (!filterDivRef.current) return;
    setIsActive((p) => !p);
    filterDivRef.current.style.height = !isActive ? '100%' : '0px';
  };

  // normalize boolean-ish values coming from the filter UI
  const asBool = (v) => {
    if (v === true || v === false) return v;
    if (v == null || v === '') return undefined;
    const s = String(v).toLowerCase();
    if (s === 'true' || s === '1') return true;
    if (s === 'false' || s === '0') return false;
    return undefined;
  };

  return (
    <>
      <Breadcrumb pageTitle={pageTitle || 'Inventory Management'} />

      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle || 'Inventory Management'}</h3>

          <div className="db-card-filter">
            <div className="db-field-down-arrow">
              <PerPageSelection
                onChangeParPage={(n, v) => setDataFilter({ ...dataFilter, [n]: v })}
                limit={dataFilter.limit}
              />
            </div>

            <button className="db-card-filter-btn table-filter-btn" onClick={handleFilterToggle}>
              <i className="lab lab-filter lab-font-size-14" />
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[{ icon: 'lab-printer-line', text: 'Print', dropDownAction: handleDownloadPdf }]}
            />

            <PermissionCheck permission="create_inventory">
              <button type="button" className="db-btn h-[37px] text-white bg-primary" onClick={() => handleOpenDrawer()}>
                <i className="lab lab-add-circle-line" /> Add Item
              </button>
            </PermissionCheck>
          </div>
        </CardHeader>

        <div className={cn('table-filter-div', isActive ? 'active' : '')} ref={filterDivRef}>
          <DataTableFilter
            onChangeFilter={(name, value) => {
              // Only "isPerishable" needs special handling; everything else stays as-is
              if (name === 'isPerishable') {
                const b = asBool(value); // true | false | undefined
                setDataFilter((prev) => {
                  const nextExtra = { ...prev.extra_filter };
                  if (b === undefined) delete nextExtra.isPerishable; // clear filter
                  else nextExtra.isPerishable = b; // UI sends raw; controller converts to service flag
                  return { ...prev, extra_filter: nextExtra, page: 1 };
                });
                return;
              }
              setDataFilter((prev) => ({ ...prev, [name]: value, page: 1 }));
            }}
          />
        </div>

        <div ref={printTableRef}>
          <DataTable
            isLoading={isLoading}
            tableColumn={tableColumn}
            dataSource={inventoryData}
            totalRecord={totalItems}
            dataFilter={dataFilter}
            onChangePage={(n, v) => setDataFilter((p) => ({ ...p, [n]: v }))}
            handleDrawerOpen={handleOpenDrawer}
            deleteRecord={handleDelete}
            showBranchCol={showBranchCol}
          />
        </div>

        <AddEditInventoryDrawer
          titleText={pageTitle || 'Inventory Management'}
          getTableData={getTableData}
          activeDrawer={drawerOpen}
          setActiveDrawer={handleCloseDrawer}
          editDataId={editDataId}
        />
      </Card>
    </>
  );
};

export default InventoryPage;
